import { QueryClient } from '@tanstack/react-query';
import { message as antMessage } from 'antd';
import dayjs from 'dayjs';
import { appointmentKeys } from '../appointment/appointment-query-keys';
import { refetchDiscussionsForStudy } from '../discussions/discussion-ws-handler';
import { tNotifications } from '../../i18n/toastI18n';
import {
  AppNotification,
  isAppointmentNotification,
  isPredictionNotification,
  NOTIFICATION_RECENT_HOURS,
  NOTIFICATION_RECENT_LIST_PARAMS,
  NotificationListResult,
  notificationKeys,
  UnifiedNotificationPayload,
} from './notifications';

export const NOTIFICATION_WS_EVENT = 'notification';

/** WS-only events — refresh other UI but do not add to persisted inbox cache. */
const EPHEMERAL_NOTIFICATION_TYPES = new Set(['prediction_started']);

export interface NotificationHandlerContext {
  queryClient: QueryClient;
  onNewInstance?: (payload: Record<string, unknown>) => void;
}

export function wsPayloadToAppNotification(
  payload: UnifiedNotificationPayload
): AppNotification | null {
  if (!payload.id || !payload.type || !payload.content) {
    return null;
  }
  if (EPHEMERAL_NOTIFICATION_TYPES.has(payload.type)) {
    return null;
  }

  return {
    id: payload.id,
    type: payload.type,
    content: payload.content,
    data: payload.data ?? null,
    priority: payload.priority,
    category: payload.category ?? null,
    is_read: payload.is_read ?? false,
    created_at: payload.created_at ?? new Date().toISOString(),
  };
}

function isWithinRecentHours(createdAt: string, hours: number): boolean {
  return dayjs(createdAt).isAfter(dayjs().subtract(hours, 'hour'));
}

function prependToListResult(
  old: NotificationListResult | undefined,
  item: AppNotification
): NotificationListResult {
  const base = old ?? {
    items: [],
    total_count: 0,
    total_all: 0,
    unread_count: 0,
    has_more: false,
  };
  if (base.items.some((n) => n.id === item.id)) {
    return base;
  }
  return {
    ...base,
    items: [item, ...base.items].slice(0, 50),
    total_count: base.total_count + 1,
    total_all: base.total_all + 1,
    unread_count: base.unread_count + (item.is_read ? 0 : 1),
  };
}

/** Merge WS notification into cache, then refetch from API for full history. */
export function upsertNotificationInCache(
  queryClient: QueryClient,
  payload: UnifiedNotificationPayload
): void {
  const item = wsPayloadToAppNotification(payload);
  if (!item) {
    return;
  }

  const listQueries = queryClient.getQueryCache().findAll({
    queryKey: [...notificationKeys.all, 'list'],
  });

  if (listQueries.length === 0) {
    queryClient.setQueryData<NotificationListResult>(
      notificationKeys.list(NOTIFICATION_RECENT_LIST_PARAMS),
      (old) => prependToListResult(old, item)
    );
  } else {
    for (const query of listQueries) {
      const params = query.queryKey[2] as { scope?: string; hours?: number } | undefined;
      if (params?.scope === 'recent') {
        const hours = params.hours ?? NOTIFICATION_RECENT_HOURS;
        if (!isWithinRecentHours(item.created_at, hours)) {
          continue;
        }
      }

      queryClient.setQueryData<NotificationListResult>(query.queryKey, (old) =>
        prependToListResult(old, item)
      );
    }
  }

  queryClient.setQueryData<number>(
    notificationKeys.unread(NOTIFICATION_RECENT_HOURS),
    (old = 0) => old + (item.is_read ? 0 : 1)
  );

  // Authoritative sync: load persisted inbox history from the API (not WS-only cache).
  void queryClient.invalidateQueries({
    queryKey: notificationKeys.list(NOTIFICATION_RECENT_LIST_PARAMS),
  });
  void queryClient.invalidateQueries({
    queryKey: notificationKeys.unread(NOTIFICATION_RECENT_HOURS),
  });
}

export function markNotificationReadInCache(
  queryClient: QueryClient,
  notificationId: string
): void {
  queryClient.setQueriesData<NotificationListResult>(
    { queryKey: [...notificationKeys.all, 'list'] },
    (old) => {
      if (!old || !Array.isArray(old.items)) {
        return old;
      }
      const wasUnread = old.items.some(
        (n) => n.id === notificationId && !n.is_read
      );
      return {
        ...old,
        items: old.items.map((n) =>
          n.id === notificationId ? { ...n, is_read: true } : n
        ),
        unread_count: wasUnread
          ? Math.max(0, old.unread_count - 1)
          : old.unread_count,
      };
    }
  );

  queryClient.setQueryData<number>(
    notificationKeys.unread(NOTIFICATION_RECENT_HOURS),
    (old = 0) => Math.max(0, old - 1)
  );
}

function extractPayload(
  messageData: Record<string, unknown>
): UnifiedNotificationPayload | null {
  if (messageData.event === NOTIFICATION_WS_EVENT) {
    const payload = messageData.data;
    if (!payload || typeof payload !== 'object') {
      return null;
    }
    return payload as UnifiedNotificationPayload;
  }

  // Legacy envelopes (pre-unification) — map to unified shape
  const legacyEvent = messageData.event as string | undefined;
  if (!legacyEvent) {
    return null;
  }

  const legacyData = (messageData.data || messageData) as Record<string, unknown>;

  if (legacyEvent === 'new_instance_received') {
    return {
      type: 'new_instance',
      category: 'dicom',
      content: (legacyData.content as string) || tNotifications('toast.newDicomInstance'),
      data: (legacyData.data as Record<string, unknown>) || legacyData,
    };
  }

  if (legacyEvent === 'prediction_complete' || legacyEvent === 'bs_prediction_complete') {
    return {
      type: 'prediction_completed',
      category: 'prediction',
      content:
        legacyEvent === 'bs_prediction_complete'
          ? tNotifications('toast.predictionBlindComplete')
          : tNotifications('toast.predictionComplete'),
      data: legacyData,
    };
  }

  if (legacyEvent === 'bs_prediction_started') {
    return {
      type: 'prediction_started',
      category: 'prediction',
      content: tNotifications('toast.predictionStarted'),
      data: legacyData,
    };
  }

  return null;
}

export function handleUnifiedNotification(
  messageData: Record<string, unknown>,
  ctx: NotificationHandlerContext
): boolean {
  const notification = extractPayload(messageData);
  if (!notification) {
    return false;
  }

  upsertNotificationInCache(ctx.queryClient, notification);

  switch (notification.category) {
    case 'scheduling':
      if (isAppointmentNotification(notification.type)) {
        ctx.queryClient.invalidateQueries({ queryKey: appointmentKeys.agendas() });
      }
      break;

    case 'dicom':
      ctx.queryClient.invalidateQueries({ queryKey: ['paginated-exams'] });
      if (notification.type === 'new_instance' && ctx.onNewInstance) {
        ctx.onNewInstance(messageData);
      }
      break;

    case 'prediction':
      ctx.queryClient.invalidateQueries({ queryKey: ['paginated-exams'] });
      if (notification.type === 'prediction_completed') {
        const isBlind =
          notification.content?.toLowerCase().includes('blind sweep') ||
          notification.content?.toLowerCase().includes('balayage aveugle');
        antMessage.success(
          isBlind
            ? tNotifications('toast.predictionBlindComplete')
            : tNotifications('toast.predictionComplete')
        );
      }
      break;

    case 'collaboration':
    case 'clinical_collaboration': {
      const studyUid = notification.data?.study_instance_uid;
      if (typeof studyUid === 'string') {
        const rootId = notification.data?.root_id ?? notification.data?.discussion_id;
        refetchDiscussionsForStudy(
          ctx.queryClient,
          studyUid,
          typeof rootId === 'string' ? rootId : undefined
        );
      }
      break;
    }

    default:
      if (isPredictionNotification(notification.type)) {
        ctx.queryClient.invalidateQueries({ queryKey: ['paginated-exams'] });
      }
      break;
  }

  return true;
}
